/**
 * Hero background canvas effect: animated monochrome grain + occasional
 * horizontal-slice glitch bands. Zero dependencies.
 *
 * Call `initHeroFx(canvas)` once after the hero canvas is in the DOM.
 * Returns a `stop()` function that cancels the rAF loop + removes the
 * resize listener. Not wired to any teardown path today (the landing
 * root gets replaced wholesale on route change and the canvas goes
 * with it), but kept as a clean handle for future use.
 *
 * Respects `prefers-reduced-motion: reduce`: draws one static grain
 * frame and skips animation + glitches.
 */

type Stop = () => void;

const TILE_SIZE = 160;           // noise tile bitmap dimension
const TARGET_FPS = 18;           // grain regen rate
const GRAIN_ALPHA = 0.22;        // per-tile draw alpha (over multiply blend)
const GLITCH_MIN_MS = 3000;      // min gap between glitch bursts
const GLITCH_MAX_MS = 8000;      // max gap between glitch bursts
const GLITCH_BANDS_MAX = 3;      // max slices per burst
const GLITCH_BAND_MIN_H = 6;     // px
const GLITCH_BAND_MAX_H = 22;    // px
const GLITCH_SHIFT_MIN = 6;      // px
const GLITCH_SHIFT_MAX = 28;     // px
const GLITCH_HOLD_MIN_MS = 60;   // how long a band stays offset
const GLITCH_HOLD_MAX_MS = 140;
const DPR_CAP = 2;

export function initHeroFx(canvas: HTMLCanvasElement): Stop {
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};
  const host = canvas.parentElement as HTMLElement | null;
  if (!host) return () => {};

  const reduceMotion =
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches;

  let tileCanvas = makeTileCanvas();
  let cssWidth = 0;
  let cssHeight = 0;

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
    cssWidth = Math.max(1, host!.clientWidth);
    cssHeight = Math.max(1, host!.clientHeight);
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx!.imageSmoothingEnabled = false;
    drawGrain();
  }

  function drawGrain() {
    if (cssWidth === 0 || cssHeight === 0) return;
    ctx!.clearRect(0, 0, cssWidth, cssHeight);
    ctx!.globalAlpha = GRAIN_ALPHA;
    // Regenerate the tile's pixels in place each frame for moving grain.
    refillTile(tileCanvas);
    // Tile across the hero area.
    for (let y = 0; y < cssHeight; y += TILE_SIZE) {
      for (let x = 0; x < cssWidth; x += TILE_SIZE) {
        ctx!.drawImage(tileCanvas, x, y);
      }
    }
    ctx!.globalAlpha = 1;
  }

  function glitchBurst() {
    if (cssWidth === 0 || cssHeight === 0) return;
    const bands = 1 + Math.floor(Math.random() * GLITCH_BANDS_MAX);
    for (let i = 0; i < bands; i++) {
      const h =
        GLITCH_BAND_MIN_H +
        Math.floor(Math.random() * (GLITCH_BAND_MAX_H - GLITCH_BAND_MIN_H));
      const y = Math.floor(Math.random() * (cssHeight - h));
      const shift =
        (GLITCH_SHIFT_MIN +
          Math.floor(Math.random() * (GLITCH_SHIFT_MAX - GLITCH_SHIFT_MIN))) *
        (Math.random() < 0.5 ? -1 : 1);
      const hold =
        GLITCH_HOLD_MIN_MS +
        Math.random() * (GLITCH_HOLD_MAX_MS - GLITCH_HOLD_MIN_MS);
      drawBandShift(y, h, shift);
      setTimeout(() => {
        // Snap the band back by re-drawing the grain over it. Easiest
        // way: repaint the whole grain (cheap) so neighbouring bands
        // don't clobber each other's restorations.
        drawGrain();
      }, hold);
    }
  }

  function drawBandShift(y: number, h: number, dx: number) {
    // Overlay an extra copy of the tile row offset by dx, tinted with
    // the accent colour, to suggest an RGB-split glitch. We don't
    // destructively read the underlying grain (Canvas CORS / perf) —
    // we just composite on top.
    ctx!.save();
    ctx!.globalCompositeOperation = "source-over";
    ctx!.globalAlpha = 0.35;
    ctx!.fillStyle = "rgba(255, 61, 20, 0.6)"; // accent red
    ctx!.fillRect(dx < 0 ? 0 : cssWidth - Math.abs(dx), y, Math.abs(dx), h);
    ctx!.globalAlpha = 0.18;
    ctx!.fillStyle = "rgba(0, 0, 0, 0.9)";
    ctx!.fillRect(0, y, cssWidth, Math.max(1, Math.floor(h / 4)));
    ctx!.restore();
  }

  // ---- animation loop ----

  let rafId = 0;
  let glitchTimer = 0;
  let lastDraw = 0;
  const frameInterval = 1000 / TARGET_FPS;

  function loop(now: number) {
    if (now - lastDraw >= frameInterval) {
      lastDraw = now;
      drawGrain();
    }
    rafId = requestAnimationFrame(loop);
  }

  function scheduleGlitch() {
    const gap = GLITCH_MIN_MS + Math.random() * (GLITCH_MAX_MS - GLITCH_MIN_MS);
    glitchTimer = window.setTimeout(() => {
      glitchBurst();
      scheduleGlitch();
    }, gap);
  }

  // ---- init ----

  resize();
  const onResize = () => resize();
  window.addEventListener("resize", onResize);

  if (!reduceMotion) {
    rafId = requestAnimationFrame(loop);
    scheduleGlitch();
  }

  return () => {
    if (rafId) cancelAnimationFrame(rafId);
    if (glitchTimer) clearTimeout(glitchTimer);
    window.removeEventListener("resize", onResize);
  };
}

// ---- noise tile ----

function makeTileCanvas(): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = TILE_SIZE;
  c.height = TILE_SIZE;
  refillTile(c);
  return c;
}

function refillTile(c: HTMLCanvasElement): void {
  const tctx = c.getContext("2d");
  if (!tctx) return;
  const img = tctx.createImageData(TILE_SIZE, TILE_SIZE);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    // Monochrome noise with slight bias toward mid-grey so multiply
    // blending doesn't crush the paper background to black.
    // Full-range monochrome noise so multiply blending carves actual
    // dark specks into the paper instead of only a neutral haze.
    const v = (Math.random() * 256) | 0;
    data[i] = v;
    data[i + 1] = v;
    data[i + 2] = v;
    // Vary per-pixel alpha so some pixels punch through more strongly
    // — gives the characteristic clumpy grain texture.
    data[i + 3] = 90 + ((Math.random() * 165) | 0);
  }
  tctx.putImageData(img, 0, 0);
}
